import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { FiArrowLeft, FiSave } from "react-icons/fi";
import { IoCloudUploadOutline } from "react-icons/io5";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import { makeRequestAuth, uploadFiles } from "../../_config/api";

function CreateBanner() {
  const navigate = useNavigate();
  const [type, setType] = useState("top");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!imageFile) return showErrorToast("Please select an image first.");

    try {
      setSubmitting(true);

      // 1. Upload the image using your function
      const uploadResult = await uploadFiles({
        files: [imageFile],
        endpoint: "/uploadImage",
        fieldName: "file",
        isMultiple: false,
      });

      const imageUrl = uploadResult.data?.imageUrl;

      if (!imageUrl) {
        showErrorToast("Upload failed. Try again.");
        return;
      }

      // 2. Save to database using your endpoint
      const response = await makeRequestAuth("post", "/admin/createBanner", {
        type: type,
        image: imageUrl,
      });

      if (
        response.status === true ||
        response.success === true ||
        response.data
      ) {
        showSuccessToast("Banner created successfully!");
        setTimeout(() => navigate("/banner"), 1500);
      } else {
        showErrorToast(response.message || "Failed to create banner");
      }
    } catch (err) {
      console.error("Error creating banner:", err);
      showErrorToast(
        err.response?.data?.message || err.message || "Server error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-4">
      <div className="max-w-2xl mx-auto bg-white dark:bg-[#1E2738] rounded-2xl p-6 md:p-8 border border-purple-300 dark:border-purple-600/50 shadow-xl">
        {/* Header */}
        <div className="flex justify-between items-center mb-8 border-b border-gray-200 dark:border-gray-700 pb-4">
          <div>
            <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Create New Banner
            </h2>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-500 hover:text-purple-600 transition"
          >
            <FiArrowLeft size={20} /> Back
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Banner Type */}
          <div>
            <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">
              Banner Type <span className="text-red-500">*</span>
            </label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-800 dark:text-white focus:ring-2 focus:ring-purple-500 outline-none transition cursor-pointer"
            >
              <option value="top">Top Banner</option>
              <option value="bottom">Bottom Banner</option>
            </select>
          </div>

          {/* Image Upload Box */}
          <div>
            <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">
              Banner Image <span className="text-red-500">*</span>
            </label>
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-8 text-center bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition relative">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                disabled={submitting}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
              />

              {imagePreview ? (
                <div className="flex flex-col items-center">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="h-32 w-auto object-cover rounded shadow-md mb-4 border border-gray-200 dark:border-gray-600"
                  />
                  <span className="text-sm text-blue-600 dark:text-blue-400 font-medium">
                    Click or drag to change image
                  </span>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center text-gray-500 dark:text-gray-400">
                  <IoCloudUploadOutline size={48} className="mb-3 opacity-50" />
                  <p className="font-medium text-gray-600 dark:text-gray-300">
                    Click to upload image
                  </p>
                  <p className="text-xs mt-1">PNG, JPG up to 5MB</p>
                </div>
              )}
            </div>
          </div>

          {/* Submit Actions */}
          <div className="flex justify-end gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-6 py-2.5 rounded-xl border border-gray-300 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex items-center gap-2 px-8 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold shadow-lg hover:scale-[1.02] transition disabled:opacity-50"
            >
              {submitting ? (
                "Saving..."
              ) : (
                <>
                  <FiSave /> Save Banner
                </>
              )}
            </button>
          </div>
        </form>
      </div>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
}

export default CreateBanner;
